/**
 * FDV — una confirmación por actualización de regalos del carrito.
 * Agrupa beneficios simultáneos en una lista legible, sin apilar avisos.
 * Solo anuncia regalos presentes en el carrito confirmado por el motor.
 * Conserva eventos de medición y cálculo de ofertas; respeta movimiento reducido.
 */

const REDUCED = (() => {
  try {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
})();

const SHOW_MS = 8000;
const LEAVE_MS = 260;
const COLLECT_MS = 160; // ventana para agrupar desbloqueos simultáneos
let notice = null;
let dismissTimer = 0;
let leaveTimer = 0;
const activeOffers = new Map();
let latestCart = null;

let stack = null;
let styled = false;
let pending = [];
let flushTimer = 0;

function injectStyles() {
  if (styled || document.getElementById('fdv-gift-toast-styles')) return;
  styled = true;
  const s = document.createElement('style');
  s.id = 'fdv-gift-toast-styles';
  s.textContent = `
  .fdv-gift-toast-stack{
    position:fixed; margin:0; inset:auto; left:50%; bottom:24px;
    transform:translateX(-50%);
    display:flex; flex-direction:column-reverse; align-items:center; gap:8px;
    width:max-content; max-width:min(92vw,420px);
    background:transparent; border:0; padding:0; overflow:visible;
    z-index:2147483002;
  }
  .fdv-gift-toast-stack::backdrop{background:transparent}
  .fdv-gift-toast{
    box-sizing:border-box; width:100%; padding:14px 40px 14px 16px; max-height:40vh; overflow:auto; position:relative;
    background:#3b2a4d; color:#efe6d2; text-align:left;
    font-family:var(--fdv-font-body,var(--font-body-family,inherit));
    font-size:14px; font-weight:500; line-height:1.4; letter-spacing:.01em;
    border:1px solid rgba(200,158,52,.45); border-radius:12px;
    box-shadow:0 8px 28px rgba(24,16,32,.32);
    opacity:0; transform:translateY(10px);
    transition:opacity .24s ease, transform .24s ease;
  }
  .fdv-gift-toast-heading{display:block;font-weight:700;margin-bottom:6px}
  .fdv-gift-toast-list{margin:0;padding-left:18px}
  .fdv-gift-toast-list li+li{margin-top:4px}
  .fdv-gift-toast-close{position:absolute;top:4px;right:4px;width:32px;height:32px;
    border:0;background:transparent;color:inherit;font-size:24px;cursor:pointer}
  .fdv-gift-toast-close:focus-visible{outline:2px solid #c89e34;outline-offset:-2px}
  .fdv-gift-toast.is-visible{opacity:1; transform:translateY(0)}
  .fdv-gift-toast.is-leaving{opacity:0; transform:translateY(10px)}
  @media (prefers-reduced-motion: reduce){
    .fdv-gift-toast{transition:none; transform:none}
  }`;
  document.head.appendChild(s);
}

function ensureStack() {
  if (stack && document.body.contains(stack)) return stack;
  injectStyles();
  stack = document.createElement('div');
  stack.className = 'fdv-gift-toast-stack';
  stack.setAttribute('role', 'status');
  stack.setAttribute('aria-live', 'polite');
  try {
    stack.setAttribute('popover', 'manual');
  } catch {
    /* atributo no soportado → fallback fixed */
  }
  document.body.appendChild(stack);
  return stack;
}

function openStack() {
  if (typeof stack.showPopover === 'function' && stack.hasAttribute('popover') && !stack.matches(':popover-open')) {
    try {
      stack.showPopover();
    } catch {
      /* ya abierto */
    }
  }
}

function closeStackIfEmpty() {
  if (!stack || stack.childElementCount > 0) return;
  if (typeof stack.hidePopover === 'function' && stack.matches(':popover-open')) {
    try {
      stack.hidePopover();
    } catch {
      /* noop */
    }
  }
}

function dismiss() {
  clearTimeout(dismissTimer);
  clearTimeout(flushTimer);
  pending = [];
  activeOffers.clear();
  const leaving = notice;
  notice = null;
  if (!leaving) return;
  clearTimeout(leaveTimer);
  leaving.classList.remove('is-visible');
  leaving.classList.add('is-leaving');
  const drop = () => { leaving.remove(); closeStackIfEmpty(); };
  if (REDUCED) drop();
  else leaveTimer = setTimeout(drop, LEAVE_MS);
}

function showSummary(offers) {
  ensureStack();
  clearTimeout(dismissTimer);
  clearTimeout(leaveTimer);
  // Actualiza una sola superficie incluso si llega otro beneficio mientras se lee.
  if (!notice || !notice.isConnected) {
    stack.replaceChildren();
    notice = document.createElement('div');
    notice.className = 'fdv-gift-toast';
    stack.appendChild(notice);
  }
  const heading = document.createElement('strong');
  heading.className = 'fdv-gift-toast-heading';
  heading.textContent = 'Tu carrito ya incluye:';
  const list = document.createElement('ul');
  list.className = 'fdv-gift-toast-list';
  const labels = new Set();
  for (const offer of offers) {
    const label = messageFor(offer);
    if (labels.has(label)) continue;
    labels.add(label);
    const li = document.createElement('li');
    li.textContent = label;
    list.appendChild(li);
  }
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'fdv-gift-toast-close';
  close.setAttribute('aria-label', 'Cerrar confirmación del carrito');
  close.textContent = '×';
  close.addEventListener('click', dismiss);
  notice.replaceChildren(heading, list, close);
  openStack();
  const current = notice;
  const enter = () => { if (current === notice) current.classList.add('is-visible'); };
  if (REDUCED) enter();
  else requestAnimationFrame(() => requestAnimationFrame(enter));
  dismissTimer = setTimeout(dismiss, SHOW_MS);
}

/* --------------------------------------------------------------- eventos -- */

function confirmed(offer, cart) {
  if (!cart) return false;
  if (!offer.giftVariantId || offer.mechanism === 'free_shipping_message') return true;
  const line = cart.items?.find(item => Number(item.id) === Number(offer.giftVariantId) && item.properties?._gift);
  if (!line || line.quantity < (offer.giftQty || 1)) return false;
  // Un regalo con descuento fallido no debe anunciarse como gratuito.
  return line.final_line_price === 0;
}

function messageFor(offer) {
  const line = latestCart?.items?.find(item => Number(item.id) === Number(offer.giftVariantId) && item.properties?._gift);
  let label = offer.rewardLabel || (line?.product_title || line?.title || '').replace(/&quot;/g, '"').replace(/&amp;/g, '&');
  if (!label) return offer.unlockedHeadline || 'Regalo añadido al carrito';
  if (line?.final_line_price === 0 && !/regalo|gratis/i.test(label)) label += ' de regalo';
  return label;
}

function onUnlocked(e) {
  const offer = e && e.detail && e.detail.offer;
  if (!offer) return;

  // discount_code / url_param también son binary, pero su feedback es la barra
  // de anuncio / el reveal de la campaña, no un toast en el carrito.
  if (offer.kind === 'binary' && offer._offer?.triggerType && offer._offer.triggerType !== 'product_in_cart') return;
  if (offer.kind !== 'binary' && offer.kind !== 'threshold') return;

  // El evento incluye el carrito final, después de agregar los regalos.
  latestCart = e.detail.cart;
  if (!confirmed(offer, latestCart)) return;
  // Un solo aviso por actualización; no elimina beneficios para imponer un cupo.
  if (!pending.some((o) => o.id === offer.id)) pending.push(offer);
  clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, COLLECT_MS);
}

function flush() {
  flushTimer = 0;
  for (const offer of pending) activeOffers.set(offer.id, offer);
  pending = [];
  for (const [id, offer] of activeOffers) {
    const state = window.FDV?.gifts?.state?.find(item => item.id === id);
    if (state?.status !== 'unlocked' || !confirmed(offer, latestCart)) activeOffers.delete(id);
  }
  const offers = [...activeOffers.values()];
  offers.sort((a, b) => (a.kind === 'threshold' ? 0 : 1) - (b.kind === 'threshold' ? 0 : 1));
  if (offers.length) showSummary(offers);
  else dismiss();
}

// Si se retira un producto o un cupón cambia la elegibilidad, el aviso abierto
// debe dejar de prometer lo que salió del carrito.
document.addEventListener('fdv:gifts:state', e => {
  latestCart = e.detail?.cart || latestCart;
  const states = e.detail?.state || [];
  pending = pending.filter(offer => states.some(state => state.id === offer.id && state.status === 'unlocked') && confirmed(offer, latestCart));
  for (const [id, offer] of activeOffers) {
    if (!states.some(state => state.id === id && state.status === 'unlocked') || !confirmed(offer, latestCart)) activeOffers.delete(id);
  }
  if (notice) {
    if (activeOffers.size) showSummary([...activeOffers.values()]);
    else dismiss();
  }
});

document.addEventListener('fdv:gift:unlocked', onUnlocked);
